import { useCallback, useEffect } from "react";
import Button from "@mui/joy/Button";
import Stack from "@mui/joy/Stack";
import {
  getHighlightedBlockNumber,
  getReferenceData,
  getTaskIdFromTaskKey,
} from "logic/logics";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { taskApi } from "api";
import { ProgramContentType } from "data/BlockComponent";
import { usePrevious } from "@uidotdev/usehooks";
import * as _ from "lodash";
import {
  thoughtRecordApi,
  THOUGHTRECORD_DOMAIN,
  THOUGHTRECORD_LIST_ENDPOINT,
} from "api/thoughtRecordApi";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { patientAtom } from "recoil/patientAtom";
import { userAtom } from "recoil/userAtom";
import { programDataAtom } from "recoil/programDataAtom";
import useGetTaskTitleFromTaskKey from "hooks/useGetTaskTitleFromTaskKey";
import useConfirmThoughtmap from "hooks/useConfirmThoughtmap";

export default function SaveButton({
  taskKey,
  data,
  thoughtRecordKey,
  translationVersion,
}: {
  taskKey: string;
  data?: ProgramContentType[];
  thoughtRecordKey?: string;
  translationVersion?: string;
}) {
  const user = useRecoilValue(userAtom);
  const previousData = usePrevious(data);
  const isThoughtRecord = taskKey?.includes("2-2-T") && thoughtRecordKey;

  const patientState = useRecoilValue(patientAtom);

  const patientIdFromTaskKey = isThoughtRecord
    ? thoughtRecordKey.split(":")[0]
    : taskKey.split(":")[1];

  const setProgramDataState = useSetRecoilState(programDataAtom);

  const taskId = getTaskIdFromTaskKey(taskKey);
  const automaticThoughtId = taskId.includes("_")
    ? taskId.split("_")[1]
    : undefined;

  const title = useGetTaskTitleFromTaskKey(taskKey);

  const queryClient = useQueryClient();

  const saveActivityQueryFn = useCallback(
    () =>
      taskApi.saveData(`${taskKey}`, {
        taskId: getTaskIdFromTaskKey(`${taskKey}`) || "",
        title: title,
        content: data || [],
        translationVersion: translationVersion,
      }),
    [data]
  );

  const { mutate: saveActivityData } = useMutation(saveActivityQueryFn);

  const saveThoughtRecordQueryFn = useCallback(
    () =>
      thoughtRecordApi.saveData(
        `${thoughtRecordKey}`,
        `${patientState?.patientId}`,
        {
          taskId: getTaskIdFromTaskKey(`${taskKey}`) || "",
          title: title,
          content: data || [],
          translationVersion: translationVersion,
          thoughtRecordKey: thoughtRecordKey,
        }
      ),
    [data]
  );

  const { mutate: saveThoughtRecordData } = useMutation(
    saveThoughtRecordQueryFn,

    {
      onSuccess: () => {
        if (
          !_.isEqual(
            getReferenceData(previousData || [], "date"),
            getReferenceData(data || [], "date")
          ) ||
          !_.isEqual(
            getReferenceData(previousData || [], "situation"),
            getReferenceData(data || [], "situation")
          ) ||
          getHighlightedBlockNumber(data || []) !==
            getHighlightedBlockNumber(previousData || [])
        ) {
          console.log("생각기록지 리스트 새로 불러와");
          queryClient.invalidateQueries([
            THOUGHTRECORD_DOMAIN,
            THOUGHTRECORD_LIST_ENDPOINT,
          ]);
        }
      },
    }
  );

  function save(data: ProgramContentType[]) {
    if (
      user?.accessToken &&
      patientState?.patientId &&
      patientState.patientId === patientIdFromTaskKey
    ) {
      if (isThoughtRecord) {
        if (
          getReferenceData(data || [], "thoughtRecordKey")?.value ===
          thoughtRecordKey
        ) {
          saveThoughtRecordData();
        }
      } else {
        saveActivityData();
      }
    } else {
      setProgramDataState({
        [getTaskIdFromTaskKey(taskKey) as string]: {
          taskId: getTaskIdFromTaskKey(taskKey),
          title: title,
          content: data || [],
          translationVersion: translationVersion,
        },
      });
    }
  }
  // const confirmThoughtmapIfChange = useConfirmThoughtmap({
  //   data: data || [],
  //   previousData: previousData,
  //   automaticThoughtId: automaticThoughtId || "",
  // });

  useEffect(() => {
    if (data && data.length > 0) {
      if (!_.isEqual(previousData, data)) {
        save(data);
      }
      // confirmThoughtmapIfChange();
    }
  }, [data]);

  return (
    <Stack
      direction="row"
      alignItems={"center"}
      spacing={1.5}
      sx={{ py: 0, display: "none" }}
    >
      <Button
        variant="outlined"
        onClick={() => {
          save(data || []);
        }}
      >
        저장하기
      </Button>
    </Stack>
  );
}
